import { DocumentCheckStatus } from '@/types/documentCheck';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from './store';

interface ChildAccordion {
  id: string;
  title: string;
  isValid: boolean;
  fields?: any[];
}

interface Accordion {
  id: number;
  title: string;
  typeOfDocument: string;
  reviewStatus: DocumentCheckStatus;
  childAccordions: ChildAccordion[];
}

interface AccordionState {
  accordions: Accordion[];
  expandedAccordion: { [parentId: number]: string | null };
  highlightedAccordions: { [parentId: number]: string[] };
  isReviewMode: { [parentId: number]: boolean };
}

const initialState: AccordionState = {
  accordions: [
    {
      id: 0,
      title: 'Document 1',
      typeOfDocument: '',
      reviewStatus: 'idle',
      childAccordions: [
        {
          id: 'common_details_0',
          title: 'Document Details',
          isValid: false,
        },
        {
          id: 'upload_0',
          title: 'Upload Content',
          isValid: false,
        },
      ],
    },
  ],
  expandedAccordion: { '0': 'common_details_0' },
  highlightedAccordions: {},
  isReviewMode: {},
};

const generateChildAccordions = (id: number, layout: any[], type: string) => {
  const middleAccordions = layout.map((section: any, index: number) => ({
    id: `document_details_${id}_${index + 1}`,
    title: section.title,
    isValid: false,
  }));

  return [
    {
      id: `common_details_${id}`,
      title: 'Document Details',
      isValid: false,
    },
    ...middleAccordions,
    {
      id: `upload_${id}`,
      title: 'Upload Content',
      isValid: false,
    },
  ];
};

const accordionSlice = createSlice({
  name: 'accordion',
  initialState,
  reducers: {
    addAccordion: (state) => {
      const newId = state.accordions.length;
      state.accordions.push({
        id: newId,
        title: `Document ${newId + 1}`,
        typeOfDocument: '',
        reviewStatus: 'idle',
        childAccordions: [
          {
            id: `common_details_${newId}`,
            title: 'Document Details',
            isValid: false,
          },
          {
            id: `upload_${newId}`,
            title: 'Upload Content',
            isValid: false,
          },
        ],
      });
    },
    removeAccordion: (state, action: PayloadAction<number>) => {
      const removedId = action.payload;

      // Remove the accordion
      state.accordions = state.accordions.filter((acc) => acc.id !== removedId);

      // Reindex everything
      reindexAccordions(state);
    },
    updateAccordionContent: (
      state,
      action: PayloadAction<{
        id: number;
        typeOfDocument: string;
        label: string;
      }>
    ) => {
      const { id, typeOfDocument, label } = action.payload;
      const accordion = state.accordions.find(
        (accordion) => accordion.id === id
      );
      if (accordion) {
        accordion.typeOfDocument = typeOfDocument;
        accordion.title = `Document ${id + 1}${label ? `: ${label}` : ''}`;
      }
    },
    updateReviewStatus: (
      state,
      action: PayloadAction<{
        id: number;
        status: DocumentCheckStatus;
      }>
    ) => {
      const accordion = state.accordions.find(
        (acc) => acc.id === action.payload.id
      );
      if (accordion) {
        accordion.reviewStatus = action.payload.status;
      }
    },
    updateChildAccordions: (
      state,
      action: PayloadAction<{ id: number; layout: any[]; type: string }>
    ) => {
      const { id, layout, type } = action.payload;

      const accordion = state.accordions.find(
        (accordion) => accordion.id === id
      );
      if (accordion) {
        accordion.childAccordions = generateChildAccordions(id, layout, type);
      }
    },
    updateChildValidStatus: (
      state,
      action: PayloadAction<{
        id: number;
        childValidities?: { childId: string; isValid: boolean }[]; // New property to handle child validity
      }>
    ) => {
      const { id, childValidities } = action.payload;
      const accordion = state.accordions.find(
        (accordion) => accordion.id === id
      );
      if (accordion) {
        // Update individual child validity if provided
        if (childValidities) {
          childValidities.forEach((childValidity) => {
            const child = accordion.childAccordions.find(
              (c) => c.id === childValidity.childId
            );
            if (child) {
              child.isValid = childValidity.isValid;
            }
          });
        }
      }
    },
    setAllChildValidStatus: (
      state,
      action: PayloadAction<{ parentId: number; isValid: boolean }>
    ) => {
      const { parentId, isValid } = action.payload;
      const accordion = state.accordions.find((acc) => acc.id === parentId);
      if (accordion) {
        accordion.childAccordions.forEach((child) => {
          child.isValid = isValid; // Set the validity status for each child
        });
      }
    },
    setExpandedAccordion: (
      state,
      action: PayloadAction<{ parentId: number; childId: string }>
    ) => {
      state.expandedAccordion[action.payload.parentId] =
        state.expandedAccordion[action.payload.parentId] ===
        action.payload.childId
          ? null
          : action.payload.childId;
    },
    setHighlightedAccordions: (
      state,
      action: PayloadAction<{
        parentId: number;
        highlightedAccordions: string[];
      }>
    ) => {
      state.highlightedAccordions[action.payload.parentId] =
        action.payload.highlightedAccordions;
    },
    setIsReviewMode: (
      state,
      action: PayloadAction<{ parentId: number; isReviewMode: boolean }>
    ) => {
      state.isReviewMode[action.payload.parentId] = action.payload.isReviewMode;
    },
    resetAccordion: (state, action: PayloadAction<number>) => {
      const parentId = action.payload;

      // Reset expandedAccordion for the specified parentId
      state.expandedAccordion[parentId] = null;

      // Reset highlightedAccordions for the specified parentId
      state.highlightedAccordions[parentId] = [];

      // Reset isReviewMode for the specified parentId
      state.isReviewMode[parentId] = false;
    },
    resetAccordionState: (state) => {
      // Reset the state to the initial state
      return initialState;
    },
    clearAccordionMetaStates: (state) => {
      state.expandedAccordion = {};
      state.highlightedAccordions = {};
      state.isReviewMode = {};
    },
  },
});

export const {
  addAccordion,
  removeAccordion,
  updateAccordionContent,
  updateReviewStatus,
  updateChildValidStatus,
  updateChildAccordions,
  setAllChildValidStatus,
  setExpandedAccordion,
  setHighlightedAccordions,
  setIsReviewMode,
  resetAccordion,
  resetAccordionState,
  clearAccordionMetaStates,
} = accordionSlice.actions;
export default accordionSlice.reducer;

const selectReviewStatusById = (state: RootState, id: number) => {
  const accordion = state.accordion.accordions.find((acc) => acc.id === id);
  return accordion ? accordion.reviewStatus : undefined; // Return undefined if not found
};

const selectChildValidityByIds = (
  state: RootState,
  parentId: number,
  childId: string
) => {
  const accordion = state.accordion.accordions.find(
    (acc) => acc.id === parentId
  );
  if (accordion) {
    const child = accordion.childAccordions.find((c) => c.id === childId);
    return child ? child.isValid : undefined; // Return the validity status or undefined if not found
  }
  return undefined; // Return undefined if the parent accordion is not found
};

export { selectChildValidityByIds, selectReviewStatusById };

function reindexAccordions(state: AccordionState): void {
  const newExpanded: AccordionState['expandedAccordion'] = {};
  const newHighlighted: AccordionState['highlightedAccordions'] = {};
  const newReviewMode: AccordionState['isReviewMode'] = {};

  state.accordions.forEach((accordion, newIndex) => {
    const oldId = accordion.id;
    const label = accordion.title.split(':')[1]?.trim(); // Preserve label if exists

    accordion.id = newIndex;
    accordion.title = `Document ${newIndex + 1}${label ? `: ${label}` : ''}`;

    accordion.childAccordions.forEach((child) => {
      const parts = child.id.split('_');
      const prefix = parts[0];

      const suffix =
        prefix === 'document' ? parts[parts.length - 1] : undefined;

      child.id =
        prefix === 'document'
          ? `document_details_${newIndex}_${suffix}`
          : `${prefix}_${newIndex}`;
    });

    // Re-map meta states
    if (state.expandedAccordion[oldId] !== undefined) {
      newExpanded[newIndex] = state.expandedAccordion[oldId];
    }
    if (state.highlightedAccordions[oldId] !== undefined) {
      newHighlighted[newIndex] = state.highlightedAccordions[oldId];
    }
    if (state.isReviewMode[oldId] !== undefined) {
      newReviewMode[newIndex] = state.isReviewMode[oldId];
    }
  });

  state.expandedAccordion = newExpanded;
  state.highlightedAccordions = newHighlighted;
  state.isReviewMode = newReviewMode;
}
